import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube } from "lucide-react";
import { BuflLogo, WuflLogo } from "./Logo";
import { useSiteSettings } from "../context/SiteSettingsContext";

/** Simple TikTok glyph — lucide has no TikTok icon in this pinned version. */
const TikTokIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
    {...props}
  >
    <path d="M12.53 2h3.14a5.4 5.4 0 0 0 5.32 5.24v3.15a8.55 8.55 0 0 1-5.32-1.85v7.72a6.24 6.24 0 1 1-6.23-6.23c.26 0 .5.02.74.06v3.24a3.06 3.06 0 1 0 2.35 2.98V2z" />
  </svg>
);

const SOCIAL_DEFS = [
  { label: "Facebook",  key: "facebook_url",  Icon: Facebook,   testId: "footer-social-facebook" },
  { label: "Instagram", key: "instagram_url", Icon: Instagram,  testId: "footer-social-instagram" },
  { label: "TikTok",    key: "tiktok_url",    Icon: TikTokIcon, testId: "footer-social-tiktok" },
  { label: "YouTube",   key: "youtube_url",   Icon: Youtube,    testId: "footer-social-youtube" },
];

export default function Footer() {
  const { settings } = useSiteSettings();
  // Only render icons for URLs that are actually filled — no more dead "#" links.
  const socials = SOCIAL_DEFS
    .map((s) => ({ ...s, href: (settings?.[s.key] || "").trim() }))
    .filter((s) => s.href);

  const tagline = (settings?.footer_tagline || "").trim();
  const legalLine = (settings?.footer_legal_line || "").trim();

  return (
    <footer
      className="mt-24 border-t border-neutral-900 bg-black"
      data-testid="site-footer"
    >
      <div className="mx-auto max-w-[1400px] px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
          <div className="md:col-span-5">
            <BuflLogo variant="footer" />
            {tagline ? (
              <p
                className="mt-4 text-sm text-neutral-500 max-w-md leading-relaxed whitespace-pre-line"
                data-testid="footer-tagline"
              >
                {tagline}
              </p>
            ) : (
              <p className="mt-4 text-sm text-neutral-500 max-w-md leading-relaxed" data-testid="footer-tagline">
                Ligue officielle de MMA au Burkina Faso.
                <br />
                Broadcast professionnel. Combattants nationaux.
                <br />
                Prochainement à l&apos;international sous{" "}
                <WuflLogo className="inline-block align-baseline" /> .
              </p>
            )}

            {socials.length > 0 && (
              <div className="mt-6 flex items-center gap-4" data-testid="footer-socials">
                {socials.map(({ label, href, Icon, testId }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    data-testid={testId}
                    className="w-10 h-10 border border-neutral-800 flex items-center justify-center text-neutral-400 hover:text-white hover:border-gold transition-colors duration-200"
                  >
                    <Icon size={16} />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="md:col-span-3">
            <h4 className="text-xs uppercase tracking-[0.25em] text-neutral-600 font-display font-bold">
              Navigation
            </h4>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <Link to="/events" className="text-neutral-300 hover:text-white">
                  Événements
                </Link>
              </li>
              <li>
                <Link to="/fighters" className="text-neutral-300 hover:text-white">
                  Combattants
                </Link>
              </li>
              <li>
                <Link to="/rankings" className="text-neutral-300 hover:text-white">
                  Rankings
                </Link>
              </li>
              <li>
                <Link to="/live" className="text-neutral-300 hover:text-white">
                  Live
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.25em] text-neutral-600 font-display font-bold">
              Ligue
            </h4>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <Link to="/rejoindre" className="text-neutral-300 hover:text-white">
                  Rejoindre
                </Link>
              </li>
              <li>
                <Link to="/actualites" className="text-neutral-300 hover:text-white">
                  Actualités
                </Link>
              </li>
              <li>
                <Link to="/academies" className="text-neutral-300 hover:text-white" data-testid="footer-academies-link">
                  Académies
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-neutral-300 hover:text-white">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.25em] text-neutral-600 font-display font-bold">
              Légal
            </h4>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <Link to="/confidentialite" className="text-neutral-300 hover:text-white">
                  Confidentialité
                </Link>
              </li>
              <li>
                <Link to="/conditions" className="text-neutral-300 hover:text-white">
                  Conditions
                </Link>
              </li>
              <li>
                <Link
                  to="/admin"
                  className="text-neutral-700 hover:text-neutral-500 text-xs uppercase tracking-widest"
                  data-testid="footer-staff-link"
                >
                  Staff
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-neutral-900 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-xs text-neutral-600 uppercase tracking-widest font-stats">
            © {new Date().getFullYear()} BUFL / WUFL — Tous droits réservés
          </p>
          <p className="text-[10px] text-neutral-700 uppercase tracking-[0.3em]" data-testid="footer-legal-line">
            {legalLine || "Burkina Faso · Broadcast édition"}
          </p>
        </div>
      </div>
    </footer>
  );
}
